import React from "react";
import { View } from "react-native";
import { MotiView } from "moti";
import { Skeleton } from "moti/skeleton";
import styled, { useTheme } from "styled-components/native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";

const PulseAnimation = {
  from: { opacity: 0.6 },
  to: { opacity: 1 },
};

const SearchResultTabletListItemSkeleton = ({
  rows,
  fromSearchResult,
  useMobileWebView,
}: {
  rows?: number;
  fromSearchResult?: boolean;
  useMobileWebView?: boolean;
}) => {
  const theme = useTheme();
  const skeletonItems = Array(rows || 3).fill(null);
  const { deviceWidth: screenWidth } = useIsFoldable();
  return (
    <Container
      transition={{ type: "timing" }}
      fromSearchResult={fromSearchResult}
      screenWidth={screenWidth}
    >
      <SectionContainer>
        {skeletonItems.map((_, index) => (
          <React.Fragment key={index}>
            {theme.isMobile && !useMobileWebView ? (
              <MobileCard
                from={PulseAnimation.from}
                animate={PulseAnimation.to}
                transition={{ loop: true, repeat: Infinity, duration: 1000 }}
              >
                <LeftSide>
                  <Row>
                    <SmallSquare />
                    <Spacing width={10} />
                    <BigSquare />
                    <Spacing width={10} />

                    <CenterLines>
                      <Skeleton
                        colorMode="light"
                        width={screenWidth < 370 ? "95%" : "100%"}
                        height={16}
                        radius={4}
                      />
                      <Spacing height={6} />
                      <Skeleton
                        colorMode="light"
                        width={screenWidth < 370 ? "95%" : "100%"}
                        height={16}
                        radius={4}
                      />
                      <Spacing height={6} />
                      <Skeleton
                        colorMode="light"
                        width={screenWidth < 370 ? "95%" : "100%"}
                        height={16}
                        radius={4}
                      />
                    </CenterLines>
                    <RightSide screenWidth={screenWidth}>
                      <SmallSquare />
                      <Spacing height={12} />
                      <Spacing height={12} />
                      <SmallSquare />
                    </RightSide>
                  </Row>

                  <Spacing height={10} />
                  <Divider />
                  <Spacing height={10} />

                  <Skeleton
                    colorMode="light"
                    width="100%"
                    height={18}
                    radius={4}
                  />
                </LeftSide>
              </MobileCard>
            ) : (
              // ✅ Existing Web / Tablet Skeleton (unchanged)
              <>
                <ItemContainer isLast={index === skeletonItems.length - 1}>
                  <TableCell>
                    <CheckboxPlaceholder
                      from={PulseAnimation.from}
                      animate={PulseAnimation.to}
                      transition={{
                        loop: true,
                        repeat: Infinity,
                        duration: 1000,
                      }}
                    />
                    <Spacer />
                  </TableCell>

                  <InfoContainer
                    from={PulseAnimation.from}
                    animate={PulseAnimation.to}
                    transition={{
                      loop: true,
                      repeat: Infinity,
                      duration: 1000,
                    }}
                  >
                    <Skeleton
                      colorMode={"light"}
                      width="100%"
                      height={16}
                      radius={4}
                    />
                  </InfoContainer>
                </ItemContainer>

                {index < skeletonItems.length - 1 && <Separator />}
              </>
            )}
          </React.Fragment>
        ))}
      </SectionContainer>
    </Container>
  );
};

export default SearchResultTabletListItemSkeleton;

const Container = styled(MotiView)<{
  fromSearchResult?: boolean;
  screenWidth?: any;
}>`
  flex-direction: column;
  border-bottom-width: 1px;
  border-bottom-color: ${({ theme }) => theme["core-01"]};
  min-width: ${({ fromSearchResult, screenWidth }) =>
    fromSearchResult ? `${screenWidth}px` : "100%"};
  width: 100%;
  overflow: hidden;
`;

const SectionContainer = styled(MotiView)`
  padding-left: ${SpaceType.Double}px;
  padding-right: ${SpaceType.Double}px;
  padding-top: ${SpaceType.Basic}px;
  padding-bottom: ${SpaceType.Basic}px;
  background-color: #ffffff;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
`;

const ItemContainer = styled(View)<{ isLast: boolean }>`
  flex-direction: row;
  align-items: center;
  height: 20px;
  padding-bottom: ${(props) => (props.isLast ? 8 : 0)}px;
  ${(props) =>
    props.isLast &&
    `
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    overflow: hidden;
  `};
`;

const TableCell = styled(View)`
  flex-direction: row;
  align-items: center;
  margin-right: 8px;
`;

const CheckboxPlaceholder = styled(MotiView)`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  background-color: #f0f0f0;
  margin-left: 0px;
`;

const Spacer = styled(View)`
  width: ${SpaceType.LargeDouble}px;
`;

const InfoContainer = styled(MotiView)`
  flex: 1;
  padding-vertical: 4px;
  padding-horizontal: 4px;
`;

const Separator = styled(View)`
  height: 1px;
  background-color: #f0f0f0;
  margin-vertical: 8px;
`;

const MobileCard = styled(MotiView)`
  flex-direction: row;
  background-color: #fff;
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 14px;
  border-width: 1px;
  border-color: #e2e2e2;
`;

const LeftSide = styled(View)`
  flex: 1;
`;

const Row = styled(View)`
  flex-direction: row;
  align-items: center;
`;

const CenterLines = styled(View)`
  align-items: center;
  width: 65%;
`;

const RightSide = styled(MotiView)<{
  screenWidth?: any;
}>`
  width: ${({ screenWidth }) => (screenWidth < 370 ? `20px` : "30px")};
  justify-content: center;
  align-items: flex-end;
`;

const SmallSquare = styled(View)`
  width: 20px;
  height: 20px;
  background: #e6e6e6;
  border-radius: 6px;
`;

const BigSquare = styled(View)`
  width: 50px;
  height: 50px;
  background: #e6e6e6;
  border-radius: 8px;
`;

const Divider = styled(View)`
  width: 100%;
  height: 1px;
  background-color: #d8d8d8;
`;

const Spacing = styled(View)<{ height?: number; width?: number }>`
  height: ${({ height }) => height ?? 0}px;
  width: ${({ width }) => width ?? 0}px;
`;
